import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDocument, useCollection, useFirestoreMutations } from '../../hooks/useFirestore'
import AlbumImages from './AlbumImages'
import SongEditor from './SongEditor'
import './AdminEditor.css'

const EMPTY_ALBUM = {
  title: '',
  artist: '',
  images: {},       // New: { front, insideFront, insideBack, back }
  coverArtUrl: '',  // Legacy: kept for backwards compatibility
  copyrightYear: '',
  sortOrder: 0
}

export default function AlbumEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const { data: existingAlbum, loading } = useDocument('albums', isNew ? null : id)
  const { data: allSongs } = useCollection('songs')
  const { add, update, remove } = useFirestoreMutations('albums')
  const songMutations = useFirestoreMutations('songs')

  const [album, setAlbum] = useState(EMPTY_ALBUM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [editingSong, setEditingSong] = useState(null)

  // Filter songs for this album
  const albumSongs = allSongs
    .filter(song => song.albumId === id)
    .sort((a, b) => (a.trackNumber || 0) - (b.trackNumber || 0))

  useEffect(() => {
    if (existingAlbum) {
      // Handle backwards compatibility: migrate coverArtUrl to images.front
      const albumData = { ...EMPTY_ALBUM, ...existingAlbum }
      if (existingAlbum.coverArtUrl && !existingAlbum.images?.front) {
        albumData.images = {
          ...albumData.images,
          front: existingAlbum.coverArtUrl
        }
      }
      setAlbum(albumData)
    }
  }, [existingAlbum])

  const handleChange = (field, value) => {
    setAlbum(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      if (isNew) {
        const newId = await add(album)
        navigate(`/admin/albums/${newId}`, { replace: true })
      } else {
        await update(id, album)
      }
      setSaving(false)
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${album.title}" and all its songs? This cannot be undone.`)) {
      return
    }

    setSaving(true)
    try {
      // Delete all songs in this album
      for (const song of albumSongs) {
        await songMutations.remove(song.id)
      }
      // Delete the album
      await remove(id)
      navigate('/admin/albums')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleAddSong = () => {
    setEditingSong({
      albumId: id,
      title: '',
      trackNumber: albumSongs.length + 1,
      mp3Url: '',
      wavDownloadUrl: '',
      durationSeconds: 0
    })
  }

  const handleEditSong = (song) => {
    setEditingSong(song)
  }

  const handleSaveSong = async (songData) => {
    try {
      if (songData.id) {
        await songMutations.update(songData.id, songData)
      } else {
        await songMutations.add(songData)
      }
      setEditingSong(null)
    } catch (err) {
      throw err
    }
  }

  const handleDeleteSong = async (songId) => {
    if (!window.confirm('Delete this song?')) return
    try {
      await songMutations.remove(songId)
    } catch (err) {
      setError(err.message)
    }
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>{isNew ? 'New Album' : 'Edit Album'}</h1>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => navigate('/admin/albums')}
        >
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="editor-form">
        {error && <div className="error-banner">{error}</div>}

        <AlbumImages
          images={album.images || {}}
          onChange={(images) => handleChange('images', images)}
        />

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="title">Album Title *</label>
            <input
              id="title"
              type="text"
              value={album.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
              placeholder="Album title"
            />
          </div>

          <div className="form-group">
            <label htmlFor="artist">Artist *</label>
            <input
              id="artist"
              type="text"
              value={album.artist}
              onChange={(e) => handleChange('artist', e.target.value)}
              required
              placeholder="Artist name"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="copyrightYear">Copyright Year</label>
            <input
              id="copyrightYear"
              type="text"
              value={album.copyrightYear}
              onChange={(e) => handleChange('copyrightYear', e.target.value)}
              placeholder="e.g., 2024"
            />
          </div>

          <div className="form-group">
            <label htmlFor="sortOrder">Sort Order</label>
            <input
              id="sortOrder"
              type="number"
              value={album.sortOrder}
              onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : (isNew ? 'Create Album' : 'Save Album')}
          </button>

          {!isNew && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete Album
            </button>
          )}
        </div>
      </form>

      {/* Songs Section - only show after album is created */}
      {!isNew && (
        <div className="songs-section">
          <div className="songs-header">
            <h3>Songs ({albumSongs.length})</h3>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleAddSong}
            >
              + Add Song
            </button>
          </div>

          {albumSongs.length === 0 ? (
            <p className="text-secondary">No songs yet. Add your first song to this album.</p>
          ) : (
            <div className="admin-list">
              {albumSongs.map(song => (
                <div key={song.id} className="admin-song-item">
                  <span className="admin-song-num">{song.trackNumber}</span>
                  <div className="admin-song-details">
                    <h4>{song.title}</h4>
                    <p>
                      {formatDuration(song.durationSeconds)}
                      {song.mp3Url && ' • MP3'}
                      {song.wavDownloadUrl && ' • WAV'}
                    </p>
                  </div>
                  <div className="admin-song-actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleEditSong(song)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleDeleteSong(song.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Song Editor Modal */}
      {editingSong && (
        <SongEditor
          song={editingSong}
          onSave={handleSaveSong}
          onCancel={() => setEditingSong(null)}
        />
      )}
    </div>
  )
}

function formatDuration(seconds) {
  if (!seconds) return '--:--'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
